import React from 'react';
import styled from 'styled-components';
import { DateAndGranularityPicker as _DateAndGranularityPicker } from '@clatter/ui';
import { CircularLoader } from '../../../shared';
import { DropdownSelect } from '../../UI';
import { SlidesThemeSelector } from '../../../pages/Presentations/components/SlidesThemeSelector';

const DateAndGranularityPickerAny = _DateAndGranularityPicker as any;

export const GROUP_BY_OPTIONS = {
  NONE: 'none',
  PPTX: 'pptx',
} as const;

export const SORT_DIRECTION_OPTIONS = {
  NEWEST: 'newest',
  OLDEST: 'oldest',
} as const;

export const SORT_BY_OPTIONS = {
  MODIFIED: 'modified',
  CREATED: 'created',
  NAME: 'name',
} as const;

export type GroupByOption = (typeof GROUP_BY_OPTIONS)[keyof typeof GROUP_BY_OPTIONS];
export type SortDirection = (typeof SORT_DIRECTION_OPTIONS)[keyof typeof SORT_DIRECTION_OPTIONS];
export type SortByOption = (typeof SORT_BY_OPTIONS)[keyof typeof SORT_BY_OPTIONS];

export interface DateRange {
  startDate: number;
  endDate: number;
}

export interface SlidesViewFilters {
  dateRange: DateRange | null;
  groupBy: GroupByOption;
  sortDirection: SortDirection;
  sortBy: SortByOption;
}

interface FolderManagerSlidesViewFiltersProps {
  filters: SlidesViewFilters;
  isFiltering?: boolean;
  viewStateKey?: string;
  showThemePicker?: boolean;
  onChange: (filters: SlidesViewFilters) => void;
}

const StyledFiltersContainer = styled.div`
  container-type: inline-size;
`;

const StyledFilters = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => theme.spacing.small} 0;

  label {
    font-size: ${({ theme }) => theme.fontSize.smaller};
    color: ${({ theme }) => theme.palette.gray};
    white-space: nowrap;
  }

  .dropdown-select {
    min-width: 120px;
  }

  .filters-start {
    min-width: 0;

    .form-control {
      padding-bottom: 0;
    }
  }

  .filters-theme {
    justify-self: center;
    width: 250px;
    flex-shrink: 0;
  }

  .filters-sort {
    justify-self: end;
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.small};
  }

  @container (max-width: 1200px) {
    grid-template-columns: 1fr 1fr;

    .filters-start {
      grid-column: 1 / -1;
    }

    .filters-theme {
      justify-self: start;
    }

    .filters-sort {
      justify-self: end;
    }
  }

  @container (max-width: 700px) {
    grid-template-columns: 1fr;

    .filters-start,
    .filters-theme,
    .filters-sort {
      justify-self: stretch;
    }
  }
`;

const SortDirectionButton = styled.button<{ $isAscending: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: 1px solid ${({ theme }) => theme.palette.border};
  border-radius: ${({ theme }) => theme.radius.small};
  cursor: pointer;
  padding: 6px 8px;
  font-size: 14px;
  line-height: 1;
  color: ${({ theme }) => theme.palette.gray};
  transition: transform 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.palette.hoverBackgroundColor};
  }

  span {
    display: inline-block;
    transition: transform 0.2s ease;
    transform: ${({ $isAscending }) => ($isAscending ? 'rotate(180deg)' : 'rotate(0deg)')};
  }
`;

export const getDateRangeFromFilters = (filters: SlidesViewFilters): DateRange | null => {
  return filters.dateRange;
};

const groupByOptions: IDropdownSelectOptionItem[] = [
  { value: GROUP_BY_OPTIONS.NONE, label: 'None' },
  { value: GROUP_BY_OPTIONS.PPTX, label: 'PPTX' },
];

const sortByOptions: IDropdownSelectOptionItem[] = [
  { value: SORT_BY_OPTIONS.MODIFIED, label: 'Modified' },
  { value: SORT_BY_OPTIONS.CREATED, label: 'Created' },
  { value: SORT_BY_OPTIONS.NAME, label: 'Name' },
];

export const FolderManagerSlidesViewFilters = ({
  filters,
  isFiltering,
  viewStateKey,
  showThemePicker = true,
  onChange,
}: FolderManagerSlidesViewFiltersProps) => {
  const handleDateRangeChange = ({ dateRange }: any) => {
    if (!dateRange?.range?.startDate || !dateRange?.range?.endDate) {
      onChange({ ...filters, dateRange: null });
      return;
    }

    onChange({
      ...filters,
      dateRange: {
        startDate: new Date(dateRange.range.startDate).getTime(),
        endDate: new Date(dateRange.range.endDate).getTime(),
      },
    });
  };

  return (
    <StyledFiltersContainer>
    <StyledFilters>
      <div className="filters-start">
        <DateAndGranularityPickerAny
          label="Modified:"
          onChange={handleDateRangeChange}
          showGranularity={false}
          defaultDateRangeValue="last-365-days"
        />

        {isFiltering && <CircularLoader size={20} testId="filters-loader" />}
      </div>

      {showThemePicker && viewStateKey && (
        <div className="filters-theme">
          <SlidesThemeSelector viewStateKey={viewStateKey} />
        </div>
      )}

      <div className="filters-sort">
          <label>Group by:</label>
          <div className="dropdown-select">
            <DropdownSelect
              options={groupByOptions}
              value={groupByOptions.find((o) => o.value === filters.groupBy) ?? null}
              onChange={(option: IDropdownSelectOptionItem) =>
                onChange({ ...filters, groupBy: (option?.value ?? GROUP_BY_OPTIONS.NONE) as GroupByOption })
              }
              isSearchable={false}
            />
          </div>

          <label>Sort by:</label>
          <div className="dropdown-select">
            <DropdownSelect
              options={sortByOptions}
              value={sortByOptions.find((o) => o.value === filters.sortBy) ?? null}
              onChange={(option: IDropdownSelectOptionItem) =>
                onChange({ ...filters, sortBy: (option?.value ?? SORT_BY_OPTIONS.MODIFIED) as SortByOption })
              }
              isSearchable={false}
            />
          </div>
          <SortDirectionButton
            $isAscending={filters.sortDirection === SORT_DIRECTION_OPTIONS.OLDEST}
            onClick={() =>
              onChange({
                ...filters,
                sortDirection:
                  filters.sortDirection === SORT_DIRECTION_OPTIONS.NEWEST
                    ? SORT_DIRECTION_OPTIONS.OLDEST
                    : SORT_DIRECTION_OPTIONS.NEWEST,
              })
            }
            title={filters.sortDirection === SORT_DIRECTION_OPTIONS.NEWEST ? 'Descending' : 'Ascending'}
          >
            <span>&#x2193;</span>
          </SortDirectionButton>
        </div>
    </StyledFilters>
    </StyledFiltersContainer>
  );
};
